/*
 *******************************************************************************
 *
 *  Filename:   @/constants/notificationTypes.js
 *
 *  Synopsis:   Web mirror of the shared notification EVENT_TYPE catalog. Maps
 *              each EVENT_TYPE to its react-feather icon, the in-app route a
 *              click should land on, and default copy. The web route is the
 *              authoritative target on this client; a notification row may also
 *              carry DATA.{screen,params} for mobile — the web route here wins
 *              for navigation in onrounds_com.
 *
 *              Keep this in sync with §6 of the notification plan and the
 *              backend `notificationCatalog.js`. Adding a new EVENT_TYPE only
 *              requires a new entry here for the web UI to render + route it.
 *
 *  Author:     Norman J. Nolasco [ Archetact Software ]
 *
 *  Created:    Tuesday, May 27, 2026
 *
 *  Copyright (c) 2026 - Archetact Software - All Rights Reserved.
 *  Unauthorized copying of this file via any medium is strictly prohibited.
 *  Proprietary and Confidential.
 *
 *******************************************************************************
 */

import {
  Bell,
  UserPlus,
  PhoneCall,
  PhoneIncoming,
  Download,
  CheckCircle,
  AlertTriangle,
  FileText,
  Send,
  Archive,
  Calendar,
  Activity,
} from "react-feather";

const INCOMING = "/or/incoming";
const IMRFS = "/or/imrfs";

// EVENT_TYPE → { icon (react-feather component), webRoute, title, body }
// `title`/`body` are fallback copy only — the stored notification row's
// TITLE/BODY (server-interpolated) are preferred when present.
export const NOTIFICATION_TYPES = {
  CASE_CREATED: {
    icon: UserPlus,
    webRoute: INCOMING,
    title: "New case created",
    body: "A new case has been created.",
  },
  PATIENT_CREATED: {
    icon: UserPlus,
    webRoute: INCOMING,
    title: "New patient created",
    body: "A new patient has been added.",
  },
  PROVIDER_ASSIGNED: {
    icon: UserPlus,
    webRoute: INCOMING,
    title: "You were assigned a case",
    body: "A case has been assigned to you.",
  },
  CALL_COMPLETED: {
    icon: PhoneCall,
    webRoute: INCOMING,
    title: "Call completed",
    body: "A patient call has completed.",
  },
  CALL_DOWNLOADED: {
    icon: Download,
    webRoute: INCOMING,
    title: "Call recording ready",
    body: "A call recording has been downloaded.",
  },
  CALL_INGESTED: {
    icon: PhoneIncoming,
    webRoute: INCOMING,
    title: "Call ingested",
    body: "A call has been ingested.",
  },
  CALL_PROCESSING_STARTED: {
    icon: Activity,
    webRoute: IMRFS,
    title: "Processing started",
    body: "A call has started processing.",
  },
  CALL_PROCESSING_FAILED: {
    icon: AlertTriangle,
    webRoute: IMRFS,
    title: "Processing failed",
    body: "Call processing failed.",
  },
  CALL_PROCESSED_REVIEW_READY: {
    icon: CheckCircle,
    webRoute: IMRFS,
    title: "Review ready",
    body: "A processed call is ready for review.",
  },
  CALL_PROCESSED_FAILED: {
    icon: AlertTriangle,
    webRoute: IMRFS,
    title: "Processing failed",
    body: "Call processing failed.",
  },
  IMRF_APPROVED: {
    icon: CheckCircle,
    webRoute: IMRFS,
    title: "Report approved",
    body: "A report has been approved.",
  },
  REPORT_SENT: {
    icon: Send,
    webRoute: IMRFS,
    title: "Report sent",
    body: "A report has been sent.",
  },
  CASE_CLOSED: {
    icon: Archive,
    webRoute: IMRFS,
    title: "Case closed",
    body: "A case has been closed.",
  },
  FOLLOWUP_SCHEDULED: {
    icon: Calendar,
    webRoute: INCOMING,
    title: "Follow-up scheduled",
    body: "A follow-up has been scheduled.",
  },
  FOLLOWUP_PROCESSED: {
    icon: FileText,
    webRoute: INCOMING,
    title: "Follow-up processed",
    body: "A follow-up call has been processed.",
  },
};

// Fallback used for unknown / future event types so the UI never breaks.
export const DEFAULT_NOTIFICATION_TYPE = {
  icon: Bell,
  webRoute: "/or/notifications",
  title: "Notification",
  body: "",
};

/**
 * Resolve the catalog entry for an EVENT_TYPE, falling back gracefully.
 * @param {string} eventType
 * @returns {{ icon: Function, webRoute: string, title: string, body: string }}
 */
export const getNotificationType = (eventType) =>
  NOTIFICATION_TYPES[eventType] || DEFAULT_NOTIFICATION_TYPE;

/**
 * Resolve the web route a notification should navigate to. A row's own
 * DATA_JSON may carry an explicit `webRoute`; otherwise use the catalog.
 * @param {object} notification - notification row (EVENT_TYPE, DATA_JSON, ...)
 * @returns {string}
 */
export const resolveNotificationRoute = (notification) => {
  if (!notification) return DEFAULT_NOTIFICATION_TYPE.webRoute;
  let data = notification.DATA_JSON;
  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch (e) {
      data = null;
    }
  }
  if (data?.webRoute) return data.webRoute;
  return getNotificationType(notification.EVENT_TYPE).webRoute;
};
